import { lockScroll, unlockScroll } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/*
 * ROTAS69 — один контроллер для всех наших <dialog>-драверов.
 *
 * Зачем. Одна и та же обёртка была скопирована в ЧЕТЫРЕ места: полоса доверия, доставка/оплата,
 * гарантии товара и гид по размеру кольца. Отличались только имена data-атрибутов, а логика
 * совпадала дословно — открытие, MutationObserver по [open], кнопки закрытия, клик по фону.
 *
 * И в каждой копии стоял запасной путь вида
 *     if (window.R69 && window.R69.lockScroll) window.R69.lockScroll(d);
 *     else document.documentElement.setAttribute('scroll-lock', '');
 * Он существовал потому, что инлайновый скрипт не может сделать import и ждёт, пока модуль
 * положит window.R69. Путь дырявый: он ставит атрибут НЕ ЗАПОМИНАЯ ВЛАДЕЛЬЦА, а настоящий
 * lockScroll держит множество владельцев — поэтому при двух открытых диалогах закрытие первого
 * снимало блокировку у обоих. Здесь файл — модуль, импортирует lockScroll напрямую, и запасной
 * путь не нужен вовсе.
 *
 * Слушатель ОДИН и делегированный на document. Так было у гида по размеру, и это правильнее
 * покомпонентной привязки: quick-add вставляет разметку после загрузки, а на телефоне блок
 * доставки вынимается из карточки товара и морфится в модалку — при делегировании ни то, ни
 * другое не требует перепривязки, и двойных слушателей возникнуть не может в принципе.
 *
 * show(), НЕ showModal(). Модальный диалог делает inert ВЕСЬ документ, и Blink пересчитывает стиль
 * всей страницы при открытии и при закрытии: тап по кнопке «доставка» на странице товара — 166 мс
 * пересчёта 1 965 элементов плюс 26 мс раскладки в обработчике клика, закрытие — 115 мс на 1 928
 * элементах (трейс 06.09.2026, Pixel-класс, CPU ×4; audit/2026-09/tap-frame-cost-2026-09-06.mjs
 * ACTION=pdp-delivery). Штатные дравера темы по той же причине давно открываются через show()
 * (assets/theme-drawer.js).
 *
 * Без top layer диалог обязан лежать на верхнем уровне <body>: внутри секции товара он остаётся в
 * её контексте наложения и уходит под шапку, затемнение и плавающий виджет отзывов, какой z-index
 * ему ни ставь — так и случилось 06.09.2026 с первой версией. Поэтому при первом открытии диалог
 * ПЕРЕНОСИТСЯ в <body> (портал), и вместе с ним переезжает его окружение: inline-переменные цвета
 * корня блока (--r69-dp-*, --r69tb-*) и классы цветовой схемы ближайшего предка (color-…), чтобы
 * наследуемые --color-* остались теми же. Всё, что давал модальный режим, воссоздано руками:
 * затемнение — один общий .r69-dialog-backdrop (base.css) на уровень ниже верхнего открытого
 * диалога, блокировка прокрутки — lockScroll, ловушка фокуса — trapFocus, Escape и клик по фону
 * закрывают верхний, фокус возвращается на кнопку. Диалоги лежат на z-index
 * calc(--layer-temporary + 10) — выше драверов темы и модалки quick-add, из которой открывается
 * гид по размеру. Фон не inert, как и у драверов темы.
 *
 * Скрипт блока доставки пишет дату в [data-r69-delivery-summary] по ссылкам, снятым при старте, —
 * ссылки переезд переживают (snippets/r69-delivery-payment-script.liquid).
 *
 * Разметку компонентов НЕ меняли: модуль знает их существующие имена атрибутов.
 */

/*
 * root   — тег компонента, внутри которого искать диалог. null = искать по всему документу
 *          (гид по размеру: кнопка и диалог — соседи из разных сниппетов, общего корня нет).
 * open   — атрибут кнопки открытия. Непустое ЗНАЧЕНИЕ = ключ нужного диалога (гарантии держат
 *          два: "delivery" и "returns"). Пустое = диалог в этом корне один.
 * dialog — атрибут самого <dialog>.
 * close  — атрибут кнопок закрытия внутри него.
 */
const GROUPS = [
  { root: 'r69-trust-band', open: 'data-r69tb-open', dialog: 'data-r69tb-drawer', close: 'data-r69tb-close' },
  { root: 'r69-delivery-payment', open: 'data-r69-delivery-open', dialog: 'data-r69-delivery-drawer', close: 'data-r69-delivery-close' },
  { root: 'r69-product-assurances', open: 'data-r69-assure-open', dialog: 'data-r69-assure-drawer', close: 'data-r69-assure-close' },
  { root: null, open: 'data-rss-guide-open', dialog: 'data-rss-guide', close: 'data-rss-guide-close' },
];

const OBSERVED = new WeakSet();
/** Открытые нами диалоги в порядке открытия; последний открытый — верхний. @type {Set<HTMLDialogElement>} */
const OPEN = new Set();
/** Кто открыл диалог — туда вернётся фокус. @type {WeakMap<HTMLDialogElement, Element>} */
const OPENERS = new WeakMap();
/** Откуда диалог переехал в <body> — чтобы кнопка того же корня находила его и после переезда. @type {WeakMap<HTMLDialogElement, Element>} */
const HOME = new WeakMap();
/** @type {HTMLElement | null} */
let backdrop = null;
/** @type {HTMLElement | null} */
let portalHost = null;

/** @returns {HTMLDialogElement | null} */
function topmost() {
  let top = null;
  for (const dialog of OPEN) if (dialog.open && dialog.isConnected) top = dialog;
  return top;
}

/**
 * Контейнер для переехавших диалогов и фона — последний ребёнок <body>, создаётся при загрузке
 * модуля. Диалоги вставляются В НЕГО, а не прямо в <body>, чтобы у переехавшего диалога не было
 * шести десятков соседей-секций: в трейсе первого открытия (06.09.2026, CPU ×4) принудительная
 * раскладка внутри syncScrollLock пересчитывала 2 274 элемента с пометкой на всех детях <body>;
 * какое именно правило это вызывало, бисекция не нашла (синтетические вставка в body и смена
 * атрибутов у последнего ребёнка стоили 0 мс), а второе открытие стоит 4–5 мс при CPU ×1.
 * Контейнер без position/z-index — контекста наложения не создаёт, диалоги лежат в корневом.
 */
function ensurePortalHost() {
  if (portalHost && portalHost.isConnected) return portalHost;
  portalHost = document.createElement('div');
  portalHost.id = 'r69-dialog-portal';
  document.body.append(portalHost);
  return portalHost;
}

function ensureBackdrop() {
  if (backdrop && backdrop.isConnected) return backdrop;
  backdrop = document.createElement('div');
  backdrop.className = 'r69-dialog-backdrop';
  backdrop.addEventListener('click', () => {
    const top = topmost();
    if (top) top.close();
  });
  ensurePortalHost().append(backdrop);
  return backdrop;
}

/** Затемнение сидит на один уровень ниже верхнего открытого диалога; без открытых — гаснет. */
function syncBackdrop() {
  const top = topmost();
  if (!top) {
    if (backdrop) backdrop.classList.remove('r69dlg-on');
    return;
  }
  const el = ensureBackdrop();
  const z = parseInt(getComputedStyle(top).zIndex, 10);
  if (Number.isFinite(z)) el.style.zIndex = String(z - 1);
  el.classList.add('r69dlg-on');
}

/**
 * Переносит диалог на верхний уровень <body> и берёт с собой его окружение: inline-переменные
 * корня блока и классы цветовой схемы ближайшего предка.
 * @param {HTMLDialogElement} dialog
 * @param {Element | null} root
 */
function portal(dialog, root) {
  if (dialog.parentElement === document.body) return;
  const from = root || dialog.parentElement;
  if (root) HOME.set(dialog, root);
  if (from) {
    // Inline custom properties корня (--r69-dp-*, --r69tb-*) — цвета блока из настроек.
    for (let i = 0; i < from.style.length; i++) {
      const name = from.style[i];
      if (name.startsWith('--') && !dialog.style.getPropertyValue(name)) {
        dialog.style.setProperty(name, from.style.getPropertyValue(name));
      }
    }
    // Классы цветовой схемы темы (color-…) с ближайшего предка — с ними едут --color-*.
    const schemeHost = from.closest('[class*="color-"]');
    if (schemeHost && !dialog.className.includes('color-')) {
      for (const cls of schemeHost.classList) if (cls.startsWith('color-')) dialog.classList.add(cls);
    }
  }
  ensurePortalHost().append(dialog);
}

/**
 * Убирает всё, что было надето при открытии, когда у диалога пропадает атрибут [open].
 *
 * Следим за атрибутом, а не за событием 'close': программный close() событие шлёт, а атрибут
 * снимается во всех случаях. Наблюдатель ставится при первом открытии и живёт дальше; повторно
 * его не заводим.
 * @param {HTMLDialogElement} dialog
 */
function watchClose(dialog) {
  if (OBSERVED.has(dialog)) return;
  OBSERVED.add(dialog);
  new MutationObserver(() => {
    if (dialog.open) return;
    unlockScroll(dialog);
    OPEN.delete(dialog);
    removeTrapFocus();
    const remaining = topmost();
    if (remaining) trapFocus(remaining);
    syncBackdrop();
    const opener = OPENERS.get(dialog);
    if (!remaining && opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

/**
 * Находит диалог, которым управляет нажатая кнопка: сперва внутри корня, потом среди уже
 * переехавших в <body> — по запомненному корню.
 * @param {{root: string | null, open: string, dialog: string}} group
 * @param {Element} opener
 * @returns {{dialog: HTMLDialogElement | null, scope: Element | null}}
 */
function dialogFor(group, opener) {
  const key = opener.getAttribute(group.open) || '';
  const selector = 'dialog[' + group.dialog + (key ? '="' + CSS.escape(key) + '"' : '') + ']';
  const candidates = /** @type {HTMLDialogElement[]} */ ([...document.querySelectorAll(selector)]);
  if (group.root) {
    const scope = opener.closest(group.root);
    if (!scope) return { dialog: null, scope: null };
    const inScope = /** @type {HTMLDialogElement | null} */ (scope.querySelector(selector));
    if (inScope) return { dialog: inScope, scope };
    for (const candidate of candidates) if (HOME.get(candidate) === scope) return { dialog: candidate, scope };
    return { dialog: null, scope };
  }
  // Без корня (гид по размеру): диалог — ближайший к кнопке. На странице кольца их два (форма
  // товара и ещё одна копия), поэтому сперва уже переехавший, чей корень содержит кнопку, потом
  // подъём по предкам до первого, в котором копия ещё лежит.
  for (const candidate of candidates) {
    const home = HOME.get(candidate);
    if (home && home.contains(opener)) return { dialog: candidate, scope: home };
  }
  for (let el = opener.parentElement; el; el = el.parentElement) {
    const found = /** @type {HTMLDialogElement | null} */ (el.querySelector(selector));
    if (found) return { dialog: found, scope: el };
  }
  return { dialog: candidates[0] || null, scope: null };
}

/**
 * Что открыли — для GA4: событие r69_info_open (в GTM — info_open с параметром info_name). Гиды по
 * размеру, доставка и возвраты показывают, в чём сомневаются перед покупкой.
 * @param {{dialog: string, open: string}} group
 * @param {Element} opener
 * @param {HTMLDialogElement} dialog
 */
function infoName(group, opener, dialog) {
  // у кнопки гида в выборе размера ключа нет — какой гид открылся, знает сам диалог
  const key = opener.getAttribute(group.open) || dialog.getAttribute(group.dialog) || '';
  if (group.dialog === 'data-rss-guide') {
    return { ring: 'size_guide_ring', len: 'size_guide_length', notify: 'notify_form' }[key] || 'guide_' + key;
  }
  if (group.dialog === 'data-r69-assure-drawer') return key || 'assurances';
  if (group.dialog === 'data-r69-delivery-drawer') return 'delivery';
  return 'trust' + (key ? '_' + key : '');
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {Element | null} scope
 * @param {Element} opener
 */
function openDialog(dialog, scope, opener) {
  watchClose(dialog);
  OPENERS.set(dialog, opener);
  portal(dialog, scope);
  dialog.show();
  OPEN.add(dialog);
  lockScroll(dialog);
  syncBackdrop();
  trapFocus(dialog);
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);
  if (!target || !target.closest) return;

  for (const group of GROUPS) {
    const opener = target.closest('[' + group.open + ']');
    if (opener) {
      const { dialog, scope } = dialogFor(group, opener);
      if (!dialog || dialog.open) return;
      event.preventDefault();
      openDialog(dialog, scope, opener);
      (window.dataLayer = window.dataLayer || []).push({ event: 'r69_info_open', info_name: infoName(group, opener, dialog) });
      return;
    }

    const closer = target.closest('[' + group.close + ']');
    if (closer) {
      const dialog = /** @type {HTMLDialogElement | null} */ (closer.closest('dialog'));
      if (dialog) dialog.close();
      return;
    }
  }
});

// Немодальный диалог по Escape сам не закрывается — закрываем верхний из наших.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  const top = topmost();
  if (!top) return;
  event.preventDefault();
  top.close();
});

// Фон создаётся заранее: вставка узла в <body> во время тапа — лишняя работа в кадре ответа.
if (document.body) ensureBackdrop();
else document.addEventListener('DOMContentLoaded', () => ensureBackdrop());
